/* Copyright (c) 2020-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ReactNode } from "react";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  Operation,
  NextLink,
  Observable,
  Reference,
  split,
} from "@apollo/client";
import { NetworkError } from "@apollo/client/errors";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createUploadLink } from "apollo-upload-client";
import { ExecutionResult, GraphQLError } from "graphql";
import { createClient } from "graphql-ws";
import { Group, PageInfo } from "~/generated/graphql";
import { auth0Client } from "~/services";
import { getCustomerFromLS } from "../CustomerSelector";

type GraphQLErrorCustom = GraphQLError & {
  errorType?: string;
};

type ErrorResponseCustom = {
  graphQLErrors?: ReadonlyArray<GraphQLErrorCustom>;
  networkError?: NetworkError;
  response?: ExecutionResult;
  operation: Operation;
  forward: NextLink;
};

async function getNewToken() {
  await auth0Client.checkSession({ ignoreCache: true });
  const token = await auth0Client.getIdTokenClaims();
  return token.__raw;
}

const errorLink = onError(
  ({ graphQLErrors, operation, forward }: ErrorResponseCustom) => {
    if (graphQLErrors != null) {
      // eslint-disable-next-line fp/no-loops
      for (const err of graphQLErrors) {
        if (err?.extensions?.code === "UNAUTHENTICATED") {
          return new Observable((observer) => {
            getNewToken()
              .then((token) => {
                const oldHeaders = operation.getContext().headers;
                operation.setContext({
                  headers: {
                    ...oldHeaders,
                    Authorization: token,
                  },
                });

                return undefined;
              })
              .then(() => {
                const subscriber = {
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                };

                // Retry last failed request
                return forward(operation).subscribe(subscriber);
              })
              .catch((error) => {
                observer.error(error);
              });
          });
        }
      }
    }
  }
);
const authLink = setContext(async (_, { headers }) => {
  try {
    // Get saved token or refresh it in case it expired
    const token = await auth0Client.getIdTokenClaims();
    const { id } = getCustomerFromLS();
    return {
      headers: {
        ...headers,
        ...(token != null && { Authorization: token.__raw }),
        "x-customer-id": id || "",
      },
    };
  } catch {
    window.location.reload();
  }
});

const retryLink = new RetryLink();

const wsLink = new GraphQLWsLink(
  createClient({
    url: `${process.env.REACT_APP_GRAPHQL_WS_ENDPOINT}`,
    connectionParams: async () => {
      const token = await auth0Client.getIdTokenClaims();
      const { id } = getCustomerFromLS();

      return {
        Authorization: token.__raw,
        "x-customer-id": id,
      };
    },
  })
);

function customFetch(uri: RequestInfo | URL, options?: RequestInit) {
  try {
    const { operationName = "" } = JSON.parse(
      options?.body?.toString() ?? "{}"
    );
    const fetchUrl = `${uri}?opname=${operationName}`;

    return fetch(fetchUrl, options);
  } catch {
    return fetch(uri, options);
  }
}

const fileUploadLink = createUploadLink({
  uri: process.env.REACT_APP_GRAPHQL_ENDPOINT,
  fetch: customFetch,
});

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        groups: {
          keyArgs: ["sort", "filter", "page", "limit"],
          merge(
            existing:
              | {
                  result: Record<string, Reference>;
                  pageInfo: PageInfo;
                }
              | undefined,
            incoming: {
              result: Group[];
              pageInfo: PageInfo;
            },
            { readField }
          ) {
            const result = {
              ...existing?.result,
              ...Object.fromEntries(
                incoming.result.map((group) => {
                  const id = readField<string>("id", group);
                  return [id, group];
                })
              ),
            };
            return {
              result,
              pageInfo: incoming.pageInfo ?? existing?.pageInfo,
            };
          },
          read(existing: {
            result: Record<string, Reference>;
            pageInfo?: PageInfo;
          }) {
            if (existing) {
              return {
                result: Object.values(existing.result),
                pageInfo: existing.pageInfo,
              };
            }
          },
        },
      },
    },
    ResourceReportPackage: {
      // Disable normalization for ResourceReportPackage. Its 'id' is not globally unique
      // (e.g., the same package ID can appear in both 'installed' and 'removed' arrays).
      // This prevents cache collisions by storing the object nested within its parent
      // instead of as a shared, normalized entity.
      keyFields: false,
    },
  },
});

const splitLink = split(
  (ds) => {
    const definition = getMainDefinition(ds.query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  authLink.concat(errorLink).concat(retryLink).concat(fileUploadLink)
);

const client = new ApolloClient({
  cache,
  link: splitLink,
  defaultOptions: {
    query: {
      errorPolicy: "all",
    },
  },
});

export function CustomApolloProvider({ children }: { children: ReactNode }) {
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
